// src/v32/components/PostOnboardingCelebration.tsx
import * as React from "react";

// Match the actual ProfileInsights type from server
interface ProfileInsights {
  widgetDisplay: string;
  celebrationMessage: string;
  attributes: any;
  profileStrength: number;
  [key: string]: any;
}

interface Props {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onDiscoverClick: () => void;
  onDetailedProfileClick: () => void;
  autoCloseDuration?: number;
  insightsData?: ProfileInsights | null;
}

export function PostOnboardingCelebration({
  isOpen,
  onOpenChange,
  onDiscoverClick,
  onDetailedProfileClick,
  autoCloseDuration = 8000,
  insightsData,
}: Props) {
  // Check if we have actual insights data
  const hasInsights = !!(
    insightsData &&
    typeof insightsData === "object" &&
    insightsData.celebrationMessage
  );

  const isQueued = false; // Not using queued state for now

  React.useEffect(() => {
    if (!isOpen || !hasInsights || !autoCloseDuration) return;
    const t = setTimeout(() => onOpenChange(false), autoCloseDuration);
    return () => clearTimeout(t);
  }, [isOpen, hasInsights, autoCloseDuration, onOpenChange]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
        {/* Header */}
        <div className="mb-3">
          <h3 className="text-xl font-semibold">You’re in! 🎉</h3>
          <p className="text-sm text-neutral-600">We’re tailoring Nura to your tastes.</p>
        </div>

        {/* Content */}
        {isQueued && (
          <div className="space-y-3">
            <p className="text-sm text-neutral-700">
              We’re preparing your taste profile now. This usually takes under a minute.
            </p>
            <div className="rounded-md bg-amber-50 p-3 text-sm">
              Feel free to explore recipes while we finish. We’ll surface recommendations automatically.
            </div>
          </div>
        )}

        {!isQueued && !hasInsights && (
          <div className="space-y-3">
            <p className="text-sm text-neutral-700">
              To unlock personalized insights, do the 3 quick taste picks.
            </p>
            <button
              onClick={onDetailedProfileClick}
              className="rounded-md bg-coral-500 px-3 py-2 text-sm font-medium text-white hover:bg-coral-600"
            >
              Complete Taste Picks
            </button>
          </div>
        )}

        {hasInsights && insightsData && (
          <div className="space-y-3">
            <div className="rounded-md bg-blue-50 p-3">
              <p className="text-sm font-medium text-blue-900">
                {insightsData.widgetDisplay}
              </p>
            </div>
            <p className="text-sm text-neutral-700">
              {insightsData.celebrationMessage}
            </p>
            {insightsData.profileStrength > 0 && (
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-neutral-600">
                  <span>Profile Strength</span>
                  <span>{insightsData.profileStrength}%</span>
                </div>
                <div className="h-2 w-full rounded-full bg-neutral-200">
                  <div
                    className="h-2 rounded-full bg-coral-500 transition-all"
                    style={{ width: `${insightsData.profileStrength}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="mt-5 flex items-center justify-between">
          <button
            onClick={() => onOpenChange(false)}
            className="rounded-md border px-3 py-2 text-sm hover:bg-neutral-50"
          >
            Close
          </button>
          <div className="space-x-2">
            <button
              onClick={onDetailedProfileClick}
              className="rounded-md border px-3 py-2 text-sm hover:bg-neutral-50"
            >
              Edit Profile
            </button>
            <button
              onClick={onDiscoverClick}
              className="rounded-md bg-black px-3 py-2 text-sm text-white hover:bg-neutral-800"
            >
              Discover Recipes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}